import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetResource } from 'hooks/useGetResource';

import { Country, ElasticSearchFacetCounts } from 'types/commons';
import { FormattedOption } from 'types/dictionaries';
import { SimpleOptionType } from 'types/filters';
import { TranslationKey } from 'types/i18next';

import { JOBS_COUNTRIES } from 'constants/endpoints';
import { FilterFields } from 'constants/enums';

import { useCountryStateCity } from '../hooks/useCountryStateCity';
import { filterOptions } from '../utils/filterOptions';
import { mapOptionsWithLabels } from '../utils/mapOptionsWithLabels';
import CitySelectWrapper from './CitySelectWrapper';
import CountrySelectWrapper from './CountrySelectWrapper';
import StateSelectWrapper from './StateSelectWrapper';

interface CountryStateCitySelectProps {
  countryTitle?: TranslationKey;
  stateTitle?: TranslationKey;
  cityTitle?: TranslationKey;
  countryValue: string | undefined;
  cityValue: string | undefined;
  stateValue: string | undefined;
  onChangeCountry: (country?: string) => void;
  onChangeState: (state?: string) => void;
  onChangeCity: (city?: string) => void;
  countryPlaceholder?: TranslationKey;
  statePlaceholder?: TranslationKey;
  cityPlaceholder?: TranslationKey;
  disabled?: boolean;
  customCountries?: {
    code: string;
    label: string;
  }[];
  className?: string;
  hasAccessToFilter: (key: string) => boolean;
  countryFacets?: ElasticSearchFacetCounts;
  stateFacets?: ElasticSearchFacetCounts;
  cityFacets?: ElasticSearchFacetCounts;
}

const CountryStateCitySelect = ({
  countryTitle = 'common.label.jobCountry',
  stateTitle = 'common.label.jobState',
  cityTitle = 'common.label.jobCity',
  countryValue,
  cityValue,
  stateValue,
  countryPlaceholder = 'common.label.selectJobCountry',
  statePlaceholder = 'common.label.selectJobState',
  cityPlaceholder = 'common.label.selectJobCity',
  customCountries,
  className,
  disabled = false,
  onChangeCountry,
  onChangeState,
  onChangeCity,
  hasAccessToFilter,
  countryFacets,
  stateFacets,
  cityFacets
}: CountryStateCitySelectProps) => {
  const { t } = useTranslation();

  const { data: countriesList, isLoading } = useGetResource<Country>({
    url: JOBS_COUNTRIES,
    isOpenApi: true,
    isDisableLoad: !!customCountries
  });

  const baseCountries =
    customCountries ||
    countriesList.map(({ code, name }) => ({
      code,
      label: name
    }));

  const countries = useMemo((): { code: string; label: string }[] => {
    const formatted: FormattedOption[] = baseCountries.map(
      ({ code, label }) => ({
        value: code,
        label
      })
    );

    const normalizedCountryValue = countryValue ? [countryValue] : undefined;

    const filtered = filterOptions(
      normalizedCountryValue,
      mapOptionsWithLabels(formatted, countryFacets),
      countryFacets
    );

    return filtered.map(({ value, label }) => ({
      code: value,
      label: String(label)
    }));
  }, [baseCountries, countryValue, countryFacets]);

  const {
    country,
    state,
    city,
    states,
    cities,
    countriesOptions,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
    isShowState,
    isShowCity,
    hasStates,
    isLoadingStates,
    isLoadingCities
  } = useCountryStateCity({
    countryValue,
    cityValue,
    stateValue,
    onChangeCountry,
    onChangeState,
    onChangeCity,
    countries
  });
  const stateSelectDisabled =
    !hasAccessToFilter(FilterFields.State) || !hasStates || disabled;

  const citySelectDisabled =
    !hasAccessToFilter(FilterFields.City) || (hasStates && !state) || disabled;

  const statesWithFacets = useMemo((): SimpleOptionType[] => {
    const formatted: FormattedOption[] = states.map(({ value, label }) => ({
      value,
      label
    }));

    const normalizedStateValue = state ? [state] : undefined;

    const filtered = filterOptions(
      normalizedStateValue,
      mapOptionsWithLabels(formatted, stateFacets),
      stateFacets
    );

    return filtered.map(({ value, label }) => ({
      value,
      key: value,
      label: String(label)
    }));
  }, [states, state, stateFacets]);

  if (!!countryFacets && countries.length === 0) {
    return null;
  }

  return (
    <>
      <CountrySelectWrapper
        value={country}
        placeholder={t(countryPlaceholder)}
        options={countriesOptions}
        onChange={handleCountryChange}
        loading={isLoading}
        className={className}
        disabled={!hasAccessToFilter(FilterFields.Country) || disabled}
        hasAccessToFilter={hasAccessToFilter(FilterFields.Country)}
        title={t(countryTitle)}
      />

      {isShowState && (
        <StateSelectWrapper
          value={state}
          placeholder={t(statePlaceholder)}
          options={statesWithFacets}
          onChange={handleStateChange}
          loading={isLoadingStates}
          className={className}
          disabled={stateSelectDisabled}
          hasAccessToFilter={hasAccessToFilter(FilterFields.State)}
          title={t(stateTitle)}
        />
      )}

      {isShowCity && (
        <CitySelectWrapper
          value={city}
          placeholder={t(cityPlaceholder)}
          options={cities}
          onChange={handleCityChange}
          loading={isLoadingCities}
          className={className}
          disabled={citySelectDisabled}
          hasAccessToFilter={hasAccessToFilter(FilterFields.City)}
          title={t(cityTitle)}
        />
      )}
    </>
  );
};

export default CountryStateCitySelect;
